import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { siteConfig } from "../../config/siteConfig";

const LEGAL_TABS = [
  { id: "aviso", label: "Aviso Legal" },
  { id: "terms", label: "Términos y Condiciones" },
  { id: "privacy", label: "Política de Privacidad" },
];

const TAB_IDS = LEGAL_TABS.map((tab) => tab.id);

const LegalPage = () => {
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState(
    TAB_IDS.includes(tabParam) ? tabParam : "terms"
  );
  const { brand, uiCopy, contact, pricing, legal, seo } = siteConfig;
  const plan = pricing.plan;

  // Sincroniza la pestaña activa con el query param (?tab=aviso|terms|privacy)
  useEffect(() => {
    if (TAB_IDS.includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [tabParam]);

  // Asegura que el usuario comience desde arriba al entrar
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans selection:bg-violet-100">
      {/* Header Estilizado */}
      <header className="py-12 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-6">
          <Link
            to="/"
            className="flex items-center gap-3 mb-8 group transition-all"
          >
            <div className="bg-gradient-to-br from-violet-500 to-purple-600 p-2 rounded-lg shadow-violet-200 shadow-lg group-hover:scale-105 transition-transform">
              <svg
                className="w-5 h-5 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                />
              </svg>
            </div>
            <div className="flex flex-col leading-none">
              <span className="text-2xl font-extrabold tracking-tight bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">
                {brand.namePrefix}
                <span className="text-violet-600">{brand.nameSuffix}</span>
              </span>
              {brand.subBrand && (
                <span className="text-[12px] font-bold text-violet-600 mt-0.5 tracking-tighter">
                  {brand.subBrand}
                </span>
              )}
            </div>
          </Link>

          <h1 className="text-4xl md:text-5xl font-black text-slate-900 tracking-tight">
            Centro{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-600 to-purple-600">
              Legal
            </span>
          </h1>
          <div className="flex items-center gap-2 mt-4 text-slate-500 font-medium">
            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
            <p className="text-sm">{uiCopy.legalLastUpdate}</p>
          </div>
        </div>
      </header>

      {/* Navegación de Tabs - Sticky */}
      <div className="sticky top-0 bg-white/80 backdrop-blur-md z-10 border-b border-slate-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-6 flex gap-6 md:gap-8 overflow-x-auto">
          {LEGAL_TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-5 text-sm font-bold transition-all relative whitespace-nowrap ${
                activeTab === tab.id
                  ? "text-violet-600"
                  : "text-slate-400 hover:text-slate-600"
              }`}
            >
              {tab.label}
              {activeTab === tab.id && (
                <div className="absolute bottom-0 left-0 w-full h-1 bg-violet-600 rounded-t-full shadow-[0_-2px_10px_rgba(124,58,237,0.3)]"></div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Contenido Dinámico */}
      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="bg-white border border-slate-200 rounded-3xl shadow-xl shadow-slate-200/60 overflow-hidden transition-all duration-500">
          <article className="p-8 md:p-12 prose prose-slate prose-violet max-w-none">
            {activeTab === "aviso" ? (
              <div className="space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-500">
                <section>
                  <div className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-violet-50 text-violet-700 font-bold text-sm mb-4">
                    1
                  </div>
                  <h2 className="text-2xl font-black text-slate-900 mb-4 mt-0">
                    Editor de la plataforma
                  </h2>
                  <p className="leading-relaxed text-slate-600 font-medium">
                    La plataforma {legal.companyName} (accesible en{" "}
                    <a
                      href={`https://${seo.domain}`}
                      className="text-violet-700 font-bold no-underline hover:underline"
                    >
                      https://{seo.domain}
                    </a>
                    ) es editada y operada por <strong>PAUL BREMOND SAS</strong>,
                    sociedad por acciones simplificada de derecho francés (SASU)
                    con capital social de 1.000 €, inscrita en el Registro
                    Mercantil (RCS) de París con el n.º 908 072 903, y con
                    domicilio social en 10 rue de la Paix, 75002 París, Francia.
                    N.º de IVA intracomunitario: FR08908072903.
                  </p>
                </section>

                <section>
                  <div className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-violet-50 text-violet-700 font-bold text-sm mb-4">
                    2
                  </div>
                  <h2 className="text-2xl font-black text-slate-900 mb-4 mt-0">
                    Director de la publicación
                  </h2>
                  <p className="leading-relaxed text-slate-600 font-medium">
                    El Presidente en ejercicio de PAUL BREMOND SAS, en su calidad de representante legal de la sociedad.
                  </p>
                </section>

                <section>
                  <div className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-violet-50 text-violet-700 font-bold text-sm mb-4">
                    3
                  </div>
                  <h2 className="text-2xl font-black text-slate-900 mb-4 mt-0">
                    Contacto
                  </h2>
                  <p className="leading-relaxed text-slate-600 font-medium">
                    Para cualquier consulta legal, técnica o comercial:{" "}
                    <a
                      href={`mailto:${contact.supportEmail}`}
                      className="text-violet-700 font-bold no-underline hover:underline"
                    >
                      {contact.supportEmail}
                    </a>
                    .
                  </p>
                </section>

                <section>
                  <div className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-violet-50 text-violet-700 font-bold text-sm mb-4">
                    4
                  </div>
                  <h2 className="text-2xl font-black text-slate-900 mb-4 mt-0">
                    Alojamiento y proveedores técnicos
                  </h2>
                  <p className="leading-relaxed text-slate-600 font-medium">
                    El sitio se aloja en la red de Cloudflare, Inc. (101
                    Townsend St, San Francisco, CA 94107, EE. UU.). La base de
                    datos y la autenticación son gestionadas por Supabase, Inc.
                    Los pagos se procesan de forma segura a través de Stripe;{" "}
                    {legal.companyName} no almacena ningún dato bancario.
                  </p>
                </section>

                <section>
                  <div className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-violet-50 text-violet-700 font-bold text-sm mb-4">
                    5
                  </div>
                  <h2 className="text-2xl font-black text-slate-900 mb-4 mt-0">
                    Marca e independencia
                  </h2>
                  <p className="leading-relaxed text-slate-600 font-medium italic border-l-4 border-violet-100 pl-4">
                    {legal.companyName} es una marca comercial operada por PAUL
                    BREMOND SAS. Es una plataforma educativa independiente: no
                    está afiliada a la AMIB, la CNBV, Banxico ni a ninguna otra
                    autoridad u organismo oficial, y no los representa.
                  </p>
                </section>

                <section>
                  <div className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-violet-50 text-violet-700 font-bold text-sm mb-4">
                    6
                  </div>
                  <h2 className="text-2xl font-black text-slate-900 mb-4 mt-0">
                    Propiedad intelectual
                  </h2>
                  <p className="leading-relaxed text-slate-600 font-medium">
                    Todos los contenidos de la plataforma están protegidos por
                    las leyes de propiedad intelectual aplicables. Para más
                    detalles, consulta la sección «Propiedad Intelectual» de
                    los{" "}
                    <button
                      onClick={() => setActiveTab("terms")}
                      className="text-violet-700 font-bold hover:underline"
                    >
                      Términos y Condiciones
                    </button>
                    .
                  </p>
                </section>
              </div>
            ) : activeTab === "terms" ? (
              <div className="space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-500">
                <section>
                  <div className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-violet-50 text-violet-700 font-bold text-sm mb-4">
                    1
                  </div>
                  <h2 className="text-2xl font-black text-slate-900 mb-4 mt-0">
                    Aceptación de los Términos
                  </h2>
                  <p className="leading-relaxed text-slate-600 font-medium">
                    Al acceder o utilizar la plataforma AMIBFigura4 by MIVX, el
                    usuario acepta cumplir con los presentes Términos y
                    Condiciones. Si el usuario no está de acuerdo con estos
                    términos, deberá abstenerse de utilizar la plataforma.
                  </p>
                </section>

                <section>
                  <div className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-violet-50 text-violet-700 font-bold text-sm mb-4">
                    2
                  </div>
                  <h2 className="text-2xl font-black text-slate-900 mb-4 mt-0">
                    Descripción del Servicio
                  </h2>
                  <p className="leading-relaxed text-slate-600 font-medium">
                    AMIBFigura4 by MIVX es una plataforma educativa digital que
                    ofrece herramientas de estudio, cuestionarios, simuladores y
                    contenidos informativos destinados a apoyar la preparación
                    de los aspirantes al examen AMIB Figura 4 (Asesor en
                    Estrategias de Inversión) en los Estados Unidos Mexicanos.
                  </p>
                </section>

                <section>
                  <div className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-violet-50 text-violet-700 font-bold text-sm mb-4">
                    3
                  </div>
                  <h2 className="text-2xl font-black text-slate-900 mb-4 mt-0">
                    Uso Permitido de la Plataforma
                  </h2>
                  <p className="leading-relaxed text-slate-600 font-medium">
                    El usuario se compromete a utilizar la plataforma
                    exclusivamente con fines educativos y personales. Queda
                    prohibido utilizar el servicio para actividades ilegales,
                    fraudulentas, o que puedan afectar el funcionamiento del
                    sistema o los derechos de otros usuarios.
                  </p>
                </section>

                <section>
                  <div className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-violet-50 text-violet-700 font-bold text-sm mb-4">
                    4
                  </div>
                  <h2 className="text-2xl font-black text-slate-900 mb-4 mt-0">
                    Registro de Cuenta
                  </h2>
                  <p className="leading-relaxed text-slate-600 font-medium italic border-l-4 border-violet-100 pl-4">
                    Para utilizar ciertas funcionalidades de la plataforma, el
                    usuario deberá crear una cuenta proporcionando información
                    veraz y actualizada. El usuario es responsable de mantener
                    la confidencialidad de sus credenciales de acceso.
                  </p>
                </section>

                <section>
                  <div className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-violet-50 text-violet-700 font-bold text-sm mb-4">
                    5
                  </div>
                  <h2 className="text-2xl font-black text-slate-900 mb-4 mt-0">
                    Propiedad Intelectual
                  </h2>
                  <p className="leading-relaxed text-slate-600 font-medium">
                    Todos los contenidos disponibles en la plataforma,
                    incluyendo textos, bases de preguntas, simuladores, diseño,
                    software y material educativo, son propiedad de
                    AMIBFigura4 by MIVX o de sus respectivos titulares y están
                    protegidos por las leyes de propiedad intelectual
                    aplicables. Queda prohibida su reproducción, distribución o
                    uso comercial sin autorización previa.
                  </p>
                </section>

                <section>
                  <div className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-violet-50 text-violet-700 font-bold text-sm mb-4">
                    6
                  </div>
                  <h2 className="text-2xl font-black text-slate-900 mb-4 mt-0">
                    Precios y Facturación
                  </h2>
                  <p className="leading-relaxed text-slate-600 font-medium">
                    El acceso total a la plataforma tiene un costo de{" "}
                    <strong>
                      {plan.currency}
                      {plan.price}
                      {plan.period}
                    </strong>{" "}
                    (o el precio vigente indicado en la página principal). Al
                    suscribirse, el usuario autoriza el cargo automático
                    mensual. AMIBFigura4 by MIVX se reserva el derecho de
                    ajustar los precios, notificando cualquier cambio con
                    antelación a través del correo electrónico registrado.
                  </p>
                </section>

                <section>
                  <div className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-violet-50 text-violet-700 font-bold text-sm mb-4">
                    7
                  </div>
                  <h2 className="text-2xl font-black text-slate-900 mb-4 mt-0">
                    No Garantía de Aprobación del Examen
                  </h2>
                  <p className="leading-relaxed text-slate-600 font-medium">
                    AMIBFigura4 by MIVX proporciona herramientas de estudio con
                    fines educativos. El uso de esta plataforma no garantiza la
                    aprobación del examen oficial AMIB Figura 4 ni la obtención
                    de la certificación correspondiente ante la Asociación
                    Mexicana de Intermediarios Bursátiles (AMIB). El resultado
                    del examen depende exclusivamente del desempeño individual
                    del usuario.
                  </p>
                </section>

                <section>
                  <div className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-violet-50 text-violet-700 font-bold text-sm mb-4">
                    8
                  </div>
                  <h2 className="text-2xl font-black text-slate-900 mb-4 mt-0">
                    Limitación de Responsabilidad
                  </h2>
                  <p className="leading-relaxed text-slate-600 font-medium">
                    AMIBFigura4 by MIVX no será responsable por daños directos,
                    indirectos, incidentales o consecuenciales derivados del uso
                    o la imposibilidad de uso de la plataforma. El usuario
                    acepta que utiliza el servicio bajo su propio riesgo.
                  </p>
                </section>

                <section>
                  <div className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-violet-50 text-violet-700 font-bold text-sm mb-4">
                    9
                  </div>
                  <h2 className="text-2xl font-black text-slate-900 mb-4 mt-0">
                    Suspensión o Terminación de Cuentas
                  </h2>
                  <p className="leading-relaxed text-slate-600 font-medium">
                    AMIBFigura4 by MIVX se reserva el derecho de suspender o
                    cancelar cuentas de usuarios que violen estos términos,
                    utilicen la plataforma de forma fraudulenta o intenten
                    acceder de forma no autorizada a los sistemas.
                  </p>
                </section>

                <section>
                  <div className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-violet-50 text-violet-700 font-bold text-sm mb-4">
                    10
                  </div>
                  <h2 className="text-2xl font-black text-slate-900 mb-4 mt-0">
                    Modificaciones del Servicio
                  </h2>
                  <p className="leading-relaxed text-slate-600 font-medium">
                    AMIBFigura4 by MIVX podrá modificar o actualizar las
                    funcionalidades de la plataforma en cualquier momento con el
                    objetivo de mejorar el servicio ofrecido.
                  </p>
                </section>

                <section>
                  <div className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-violet-50 text-violet-700 font-bold text-sm mb-4">
                    11
                  </div>
                  <h2 className="text-2xl font-black text-slate-900 mb-4 mt-0">
                    Aviso Legal y Fuentes de Información
                  </h2>

                  <p className="leading-relaxed text-slate-600 font-medium mb-4">
                    Este sitio web ha sido desarrollado utilizando información
                    pública disponible de la Asociación Mexicana de
                    Intermediarios Bursátiles (AMIB), de la Comisión Nacional
                    Bancaria y de Valores (CNBV) y de la legislación financiera
                    mexicana, específicamente el
                    <strong>
                      {" "}
                      programa oficial de la Figura 4 — Asesor en Estrategias
                      de Inversión.
                    </strong>
                  </p>

                  <p className="leading-relaxed text-slate-600 font-medium mb-4">
                    AMIBFigura4 by MIVX no está afiliado, asociado ni representa
                    oficialmente a la AMIB, a la CNBV, a Banxico, a la SHCP ni
                    a ninguna otra autoridad financiera mexicana.
                  </p>

                  <p className="leading-relaxed text-slate-600 font-medium italic border-l-4 border-violet-100 pl-4 mb-4">
                    El contenido disponible en esta plataforma tiene únicamente
                    fines educativos e informativos y no reemplaza los cursos
                    oficiales requeridos por la AMIB para obtener la
                    certificación Figura 4.
                  </p>

                  <p className="leading-relaxed text-slate-600 font-medium">
                    La información se proporciona "tal cual" y AMIBFigura4 by
                    MIVX no garantiza su exactitud, integridad o actualización.
                    Para obtener información oficial se recomienda consultar
                    directamente a la AMIB, la CNBV y demás autoridades
                    competentes.
                  </p>
                </section>

                <section>
                  <div className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-red-50 text-red-600 font-bold text-sm mb-4">
                    12
                  </div>
                  <h2 className="text-2xl font-black text-slate-900 mb-4 mt-0">
                    Política de Cancelación y Reembolso
                  </h2>
                  <div className="space-y-4 text-slate-600 font-medium">
                    <p className="leading-relaxed">
                      <strong>Cancelación:</strong> El usuario puede cancelar su
                      suscripción en cualquier momento desde su panel de
                      configuración. Al confirmar la cancelación, la suscripción
                      no se renovará y el acceso a las funciones premium de la
                      plataforma se mantiene{" "}
                      <strong>
                        hasta el final del periodo de facturación ya pagado
                      </strong>
                      , momento en el que finalizará sin cargos adicionales.
                    </p>
                    <p className="leading-relaxed border-l-4 border-red-100 pl-4 italic">
                      <strong>No Reembolso:</strong> Debido a la naturaleza
                      digital del contenido y el acceso instantáneo a la base de
                      datos de preguntas y simuladores,{" "}
                      <strong>
                        no se realizarán reembolsos totales ni parciales
                      </strong>{" "}
                      bajo ninguna circunstancia una vez procesado el pago,
                      salvo disposición legal imperativa en contrario aplicable
                      al consumidor.
                    </p>
                  </div>
                </section>

                <section>
                  <div className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-violet-50 text-violet-700 font-bold text-sm mb-4">
                    13
                  </div>
                  <h2 className="text-2xl font-black text-slate-900 mb-4 mt-0">
                    Legislación Aplicable
                  </h2>
                  <p className="leading-relaxed text-slate-600 font-medium">
                    Estos términos se rigen por las leyes de los Estados Unidos
                    Mexicanos. Cualquier controversia relacionada con el uso de
                    la plataforma será resuelta conforme a la legislación
                    mexicana vigente y la jurisdicción de los tribunales
                    competentes en la Ciudad de México.
                  </p>
                </section>
              </div>
            ) : (
              <div className="space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-500">
                <section>
                  <div className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-violet-50 text-violet-700 font-bold text-sm mb-4">
                    1
                  </div>
                  <h2 className="text-2xl font-black text-slate-900 mb-4 mt-0">
                    Introducción
                  </h2>
                  <p className="leading-relaxed text-slate-600 font-medium">
                    AMIBFigura4 by MIVX es una plataforma educativa diseñada
                    para ayudar a los usuarios a prepararse para el examen AMIB
                    Figura 4 (Asesor en Estrategias de Inversión) en México.
                    Esta política explica cómo recopilamos, utilizamos y
                    protegemos la información personal de nuestros usuarios
                    conforme a la Ley Federal de Protección de Datos Personales
                    en Posesión de los Particulares (LFPDPPP) y demás
                    disposiciones aplicables.
                  </p>
                </section>

                <section>
                  <div className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-violet-50 text-violet-700 font-bold text-sm mb-4">
                    2
                  </div>
                  <h2 className="text-2xl font-black text-slate-900 mb-4 mt-0">
                    Responsable del Tratamiento
                  </h2>
                  <p className="leading-relaxed text-slate-600 font-medium mb-4">
                    El responsable del tratamiento de los datos personales es{" "}
                    <strong>
                      PAUL BREMOND SAS (10 rue de la Paix, 75002 París, Francia
                      — RCS París 908 072 903)
                    </strong>
                    , operadora de {legal.companyName}, conforme a la Ley
                    Federal de Protección de Datos Personales en Posesión de los
                    Particulares (LFPDPPP) y, en lo aplicable al operador
                    establecido en la Unión Europea, al Reglamento General de
                    Protección de Datos (RGPD).
                  </p>
                  <p className="leading-relaxed text-slate-600 font-medium">
                    Nos comprometemos a garantizar la confidencialidad,
                    seguridad y uso adecuado de la información recopilada dentro
                    de nuestra plataforma educativa. Para ejercer tus derechos,
                    contáctanos en{" "}
                    <a
                      href={`mailto:${contact.supportEmail}`}
                      className="text-violet-700 font-bold no-underline hover:underline"
                    >
                      {contact.supportEmail}
                    </a>
                    .
                  </p>
                </section>

                <section>
                  <div className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-violet-50 text-violet-700 font-bold text-sm mb-4">
                    3
                  </div>
                  <h2 className="text-2xl font-black text-slate-900 mb-4 mt-0">
                    Datos que Recopilamos
                  </h2>
                  <p className="leading-relaxed text-slate-600 font-medium">
                    Recopilamos únicamente la información necesaria para el
                    funcionamiento de la plataforma, incluyendo nombre, correo
                    electrónico, progreso de estudio, resultados de
                    cuestionarios y datos técnicos como dirección IP o tipo de
                    dispositivo utilizado para acceder al servicio.
                  </p>
                </section>

                <section>
                  <div className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-violet-50 text-violet-700 font-bold text-sm mb-4">
                    4
                  </div>
                  <h2 className="text-2xl font-black text-slate-900 mb-4 mt-0">
                    Uso de la Información
                  </h2>
                  <p className="leading-relaxed text-slate-600 font-medium border-l-4 border-violet-100 pl-4">
                    Utilizamos la información recopilada para gestionar las
                    cuentas de usuario, mostrar el progreso de estudio, mejorar
                    la experiencia educativa, ofrecer soporte técnico y
                    optimizar el funcionamiento de la plataforma.
                  </p>
                </section>

                <section>
                  <div className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-violet-50 text-violet-700 font-bold text-sm mb-4">
                    5
                  </div>
                  <h2 className="text-2xl font-black text-slate-900 mb-4 mt-0">
                    Seguridad de los Datos
                  </h2>
                  <p className="leading-relaxed text-slate-600 font-medium">
                    Implementamos medidas técnicas y organizativas para proteger
                    la información personal de nuestros usuarios contra accesos
                    no autorizados, pérdidas o alteraciones. Nuestra plataforma
                    utiliza protocolos de seguridad modernos para garantizar la
                    protección de los datos.
                  </p>
                </section>

                <section>
                  <div className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-violet-50 text-violet-700 font-bold text-sm mb-4">
                    6
                  </div>
                  <h2 className="text-2xl font-black text-slate-900 mb-4 mt-0">
                    Compartición de Información
                  </h2>
                  <p className="leading-relaxed text-slate-600 font-medium">
                    AMIBFigura4 by MIVX no vende ni comercializa la información
                    personal de los usuarios. Los datos únicamente podrán ser
                    compartidos cuando sea necesario para cumplir con
                    obligaciones legales o para el funcionamiento técnico de la
                    plataforma mediante proveedores de servicios tecnológicos.
                  </p>
                </section>

                <section>
                  <div className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-violet-50 text-violet-700 font-bold text-sm mb-4">
                    7
                  </div>
                  <h2 className="text-2xl font-black text-slate-900 mb-4 mt-0">
                    Derechos del Usuario
                  </h2>
                  <p className="leading-relaxed text-slate-600 font-medium">
                    Los usuarios tienen derecho a acceder, rectificar,
                    actualizar o solicitar la eliminación de sus datos
                    personales. También pueden revocar el consentimiento
                    otorgado para el tratamiento de sus datos en cualquier
                    momento contactando a nuestro equipo de soporte.
                  </p>
                </section>

                <section>
                  <div className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-violet-50 text-violet-700 font-bold text-sm mb-4">
                    8
                  </div>
                  <h2 className="text-2xl font-black text-slate-900 mb-4 mt-0">
                    Conservación de los Datos
                  </h2>
                  <p className="leading-relaxed text-slate-600 font-medium">
                    Conservamos los datos personales únicamente durante el
                    tiempo necesario para cumplir con las finalidades educativas
                    de la plataforma o con las obligaciones legales aplicables
                    en los Estados Unidos Mexicanos.
                  </p>
                </section>

                <section>
                  <div className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-violet-50 text-violet-700 font-bold text-sm mb-4">
                    9
                  </div>
                  <h2 className="text-2xl font-black text-slate-900 mb-4 mt-0">
                    Uso de Cookies
                  </h2>
                  <p className="leading-relaxed text-slate-600 font-medium">
                    Nuestra plataforma puede utilizar cookies o tecnologías
                    similares para mejorar la experiencia de navegación,
                    recordar preferencias del usuario y analizar el uso de los
                    servicios ofrecidos.
                  </p>
                </section>

                <section>
                  <div className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-violet-50 text-violet-700 font-bold text-sm mb-4">
                    10
                  </div>
                  <h2 className="text-2xl font-black text-slate-900 mb-4 mt-0">
                    Cambios en la Política
                  </h2>
                  <p className="leading-relaxed text-slate-600 font-medium">
                    AMIBFigura4 by MIVX se reserva el derecho de modificar esta
                    política de privacidad en cualquier momento. Las
                    actualizaciones serán publicadas en la plataforma y entrarán
                    en vigor desde el momento de su publicación.
                  </p>
                </section>
              </div>
            )}
          </article>
        </div>

        {/* Footer de Página Legal */}
        <div className="mt-16 flex flex-col items-center gap-6">
          <Link
            to="/"
            className="flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-full font-bold hover:bg-violet-600 transition-all shadow-lg hover:shadow-violet-200 active:scale-95"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Volver al Inicio
          </Link>
          <p className="text-slate-400 text-sm font-medium">
            © {new Date().getFullYear()} {brand.name} {brand.subBrand} —
            Operado por PAUL BREMOND SAS.
          </p>
        </div>
      </main>
    </div>
  );
};

export default LegalPage;
